/**
 * Manage data for Projects in Active state across various Dashboards.
 */
import { DataSource } from '@client/core/data/concerns/data-source';
import { fromJson } from '@client/core/data/concerns/parses-from-json';
import { HttpResponse } from '@client/core/http/types';
import { DashboardProject } from '@client/features/dashboards/models/dashboard-project';
import { ProjectStage } from '@client/features/project/types';

/**
 * Pass these keys when loading the store to add that data to the Response.
 */
export enum AvailableData {
  Project = 'project',
  ProjectCard = 'project_card',
  LatestProjectStatus = 'latest_project_status',
  Milestones = 'milestones',
  FlipAnalysis = 'flip_analysis',
  RentalAnalysis = 'rental_analysis',
  RepairEstimate = 'repair_estimate',
  Budget = 'budget',
  TasksSummary = 'tasks_summary',
  RisksSummary = 'risks_summary',
}

interface ActiveProjectDashboardData {
  projectKanbanViewMode: 'default' | 'compact';
  projects: DashboardProject[];
}

function toMakeQueryString(keys: AvailableData[]): string {
  if (keys.length === 0) {
    return '';
  }

  return '?' + keys.map((key) => 'make[]=' + key).join('&');
}

export class ActiveProjectDashboardDataSource extends DataSource {
  /**
   * Load the store. Pass a list of the data keys you want to load.
   * This allows the client to only load data it plans to use, leading to faster
   * and smaller responses.
   *
   * The "project" key is always loaded from the backend.
   */
  async load(returnKeys?: AvailableData[]): Promise<HttpResponse<ActiveProjectDashboardData>> {
    const qs = returnKeys ? toMakeQueryString(returnKeys) : '';
    const uri = `/dashboards/projects${qs}`;

    const response = await this.httpClient.get<HttpResponse<ActiveProjectDashboardData>>(uri);

    return {
      data: {
        projectKanbanViewMode: response.data.data.projectKanbanViewMode,
        projects: response.data.data.projects.map((json) => fromJson(DashboardProject, json)),
      },
    };
  }

  /**
   * Save the given Project with a new Project Stage.
   *
   * Pass a list of the data keys you want to load.
   * This allows the client to only load data it plans to use, leading to faster
   * and smaller responses.
   */
  async changeStage(
    dp: DashboardProject,
    stage: ProjectStage,
    returnKeys?: AvailableData[],
  ): Promise<HttpResponse<DashboardProject[]>> {
    const uuid = dp.project.uuid;
    const qs = returnKeys ? toMakeQueryString(returnKeys) : '';
    const uri = `/dashboards/projects/${uuid}/stage${qs}`;

    const payload = { stage };
    const response = await this.httpClient.patch<HttpResponse<DashboardProject[]>>(uri, payload);

    return {
      data: response.data.data.map((json) => fromJson(DashboardProject, json)),
    };
  }
}
